import { intersection, isEmpty, isNull, toNumber } from 'lodash'
import { computed, toValue } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import { UNSELECTED_DATE } from '@/utils/date'
import { ACTIVE_INTERVAL_TYPE_IDS } from '@/utils/interval'
import { ALL_STATUS_OPTIONS, HOMEPAGE_OKR_SEARCH_TYPE_IDS, OKR_STATUSES } from '@/utils/objectives'
import {
  DEFAULT_CUSTOM_RANGE_VALUE,
  isValidDatesArray,
  NEW_HP_FILTERS_KEYS,
  normalizeCustomRange,
  restoreFilterValue,
  saveFilterValues
} from '@/utils/okr-elements/filters'

export const HOME_PAGE_INTERVAL_FILTER_VALUES = {
  ALL_CURRENT: ACTIVE_INTERVAL_TYPE_IDS.ALL_CURRENT,
  QUARTER: ACTIVE_INTERVAL_TYPE_IDS.QUARTER,
  YEAR: ACTIVE_INTERVAL_TYPE_IDS.YEAR,
  CUSTOM_RANGE: -1
}

export const USER_LIST_TYPES = {
  OWNERS: 'owners',
  FOREIGN_OBJECTIVES: 'foreignObjectives',
  STAKEHOLDERS: 'stakeholders',
  WATCHERS: 'watchers',
  METRICS_WITHOUT_PARENTS: 'metricsWithoutParents',
  METRICS_WITHOUT_KPI: 'metricsWithoutKpi'
}

export const GROUP_LIST_TYPES = {
  OWNERS: 'owners',
  FOREIGN_OBJECTIVES: 'foreignObjectives'
}

export const HOMEPAGE_SIDEBAR_LIST_ITEM_HEIGHT = 40
export const HOMEPAGE_SIDEBAR_LIST_ITEM_GAP = 6

export const HOMEPAGE_TABS = {
  OBJECTIVES: {
    value: 'objectives',
    label: 'dashboard.objectives'
  },
  CHECK_INS: {
    value: 'check-ins',
    label: 'menu.check_ins'
  },
  DASHBOARD: {
    value: 'dashboard',
    label: 'menu.dashboard'
  }
}

export const SEARCH_TYPE_OPTIONS = [
  {
    label: 'homepage.progress.switch.objectives',
    value: HOMEPAGE_OKR_SEARCH_TYPE_IDS.OBJECTIVES,
    color: 'var(--primary-color-next)',
    gapKey: 'objGap'
  },
  {
    label: 'homepage.progress.switch.krs',
    value: HOMEPAGE_OKR_SEARCH_TYPE_IDS.KEY_RESULTS,
    color: 'var(--primary-color-next)',
    gapKey: 'krGap'
  }
]

export const getAvailableCustomRangeParams = ({ selectedDates, intervalIds }) => {
  if (isEmpty(intervalIds)) {
    const { startDates, dueDates } = selectedDates
    return {
      startDateFrom: startDates ? startDates[0] : UNSELECTED_DATE,
      dueDateTo: dueDates ? dueDates[1] : UNSELECTED_DATE
    }
  } else {
    return {
      startDateFrom: UNSELECTED_DATE,
      dueDateTo: UNSELECTED_DATE
    }
  }
}

export const useRouteWorkspaceIds = ({ skipLsRestoring = false } = {}) => {
  const route = useRoute()
  const store = useStore()

  const resolveHomepageWorkspaceIds = () => {
    const defaultValue = [store.state.workspaces.workspaceId]

    let routeWorkspaceIds = restoreFilterValue(
      route,
      NEW_HP_FILTERS_KEYS.WORKSPACE_IDS,
      [...defaultValue],
      skipLsRestoring
    )

    if (!Array.isArray(routeWorkspaceIds) || isEmpty(routeWorkspaceIds)) {
      routeWorkspaceIds = [...defaultValue]
    }

    let crossValidatedWorkspaceIds = intersection(
      routeWorkspaceIds.map(toNumber),
      store.state.workspaces.workspaces.map(workspace => workspace.id)
    )

    if (isEmpty(crossValidatedWorkspaceIds)) {
      crossValidatedWorkspaceIds = [...defaultValue]
    }

    return crossValidatedWorkspaceIds
  }

  return {
    resolveHomepageWorkspaceIds
  }
}

/*
 * Resolves the interval filter of the homepage from restored (query or LS) values.
 *
 * Dates win over the interval type: the old homepage kept its intervals under a key
 * the new homepage doesn't read, so a saved custom range is the only interval state
 * that survives the migration, and CUSTOM_RANGE without dates is meaningless.
 * Only "start date from" and "due date to" are used by the custom range,
 * the other positions of both pairs are always UNSELECTED_DATE.
 */
export const resolveHomepageIntervalFilters = ({ intervalTypeId, startDates, dueDates }) => {
  const [startDateFrom] = isValidDatesArray(startDates)
    ? normalizeCustomRange(startDates)
    : DEFAULT_CUSTOM_RANGE_VALUE
  const [, dueDateTo] = isValidDatesArray(dueDates)
    ? normalizeCustomRange(dueDates)
    : DEFAULT_CUSTOM_RANGE_VALUE

  if (!isNull(startDateFrom) || !isNull(dueDateTo)) {
    return {
      intervalTypeId: HOME_PAGE_INTERVAL_FILTER_VALUES.CUSTOM_RANGE,
      startDates: [startDateFrom, UNSELECTED_DATE],
      dueDates: [UNSELECTED_DATE, dueDateTo]
    }
  }

  const isValidIntervalType = Object.values(ACTIVE_INTERVAL_TYPE_IDS).includes(intervalTypeId)

  return {
    intervalTypeId: isValidIntervalType
      ? intervalTypeId
      : HOME_PAGE_INTERVAL_FILTER_VALUES.ALL_CURRENT,
    startDates: [...DEFAULT_CUSTOM_RANGE_VALUE],
    dueDates: [...DEFAULT_CUSTOM_RANGE_VALUE]
  }
}

const HOMEPAGE_GRADE_TYPES_ORDER = [
  OKR_STATUSES.NOT_STARTED,
  OKR_STATUSES.AT_RISK,
  OKR_STATUSES.BEHIND,
  OKR_STATUSES.ON_TRACK,
  OKR_STATUSES.CLOSED,
  OKR_STATUSES.ABANDONED
]

export const DEFAULT_GRADE_TYPES = [
  OKR_STATUSES.NOT_STARTED,
  OKR_STATUSES.ON_TRACK,
  OKR_STATUSES.BEHIND,
  OKR_STATUSES.AT_RISK,
  OKR_STATUSES.CLOSED
]

export const DEFAULT_GRADE_TYPES_ON_RESET = [...DEFAULT_GRADE_TYPES]

export const HOMEPAGE_GRADE_TYPES_OPTIONS = HOMEPAGE_GRADE_TYPES_ORDER.reduce(
  (options, gradeType) => {
    const option = ALL_STATUS_OPTIONS.find(option => option.value === gradeType)

    if (!option) {
      return options
    }

    return [...options, { ...option }]
  },
  []
)

export const useRouteGradeTypes = () => {
  const route = useRoute()

  const resolveHomepageGradeTypes = () => {
    const defaultValue = DEFAULT_GRADE_TYPES

    let routeGradeTypes = restoreFilterValue(route, NEW_HP_FILTERS_KEYS.GRADE_TYPES, [
      ...defaultValue
    ])

    if (!Array.isArray(routeGradeTypes) || isEmpty(routeGradeTypes)) {
      routeGradeTypes = [...defaultValue]
    }

    let crossValidatedGradeTypes = intersection(
      routeGradeTypes.map(toNumber),
      HOMEPAGE_GRADE_TYPES_ORDER
    )

    if (isEmpty(crossValidatedGradeTypes)) {
      crossValidatedGradeTypes = [...defaultValue]
    }

    return crossValidatedGradeTypes
  }

  return {
    resolveHomepageGradeTypes
  }
}

// writes the whole homepage filters object to the query and LS at once
export const useHomepageFiltersPersistence = filterValues => {
  const route = useRoute()
  const router = useRouter()

  const persistFilters = async () => {
    await router.isReady()

    await saveFilterValues(
      router,
      route,
      Object.keys(toValue(filterValues)),
      Object.values(toValue(filterValues))
    )
  }

  return {
    persistFilters
  }
}

export const HOMEPAGE_OBJECTIVES_CARD_STATE_KEYS = {
  OBJECTIVES: 'objectives',
  METRICS: 'metrics',
  STAKEHOLDERS: 'stakeholders'
}

export const useHomepageSidebarFilteredOutTooltip = (token = '', qty = 0) => {
  const { t } = useI18n()

  const filteredOutItemConfig = computed(() => {
    return {
      content: t(toValue(token), toValue(qty)),
      placement: 'top',
      delay: 500,
      maxWidth: 240
    }
  })

  return {
    filteredOutItemConfig
  }
}

export const useHomepageProgressExploring = () => {
  const store = useStore()

  const canExploreOtherPeopleProgress = computed(() => {
    return !store.getters['system/blockExploringOtherPeopleProgressOnHomepage']
  })

  return {
    canExploreOtherPeopleProgress
  }
}
